import { Capacitor } from '@capacitor/core';
import { buildExternalWebUrl } from '@/utils/publicShareUrls';
import { env } from '@/lib/env';

const APPLE_AUTH_SCRIPT_ID = 'apple-signin-js';
const APPLE_AUTH_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const DEFAULT_APPLE_WEB_CLIENT_ID = 'com.pevr2wv0gtxs.p3gfapng2app.auth';
const DEFAULT_APPLE_WEB_REDIRECT_PATH = '/auth';
const DEFAULT_NATIVE_APPLE_WEB_REDIRECT_URL = 'https://journalparty.com/auth';

type AppleName = {
  firstName?: string;
  lastName?: string;
};

type AppleUser = {
  email?: string;
  name?: AppleName;
};

type AppleSignInResponse = {
  authorization?: {
    id_token?: string;
  };
  user?: AppleUser;
};

type AppleAuthConfig = {
  clientId: string;
  scope: string;
  redirectURI: string;
  state: string;
  nonce: string;
  usePopup: boolean;
};

type AppleAuth = {
  init: (config: AppleAuthConfig) => void;
  signIn: (config?: Partial<AppleAuthConfig>) => Promise<AppleSignInResponse>;
};

declare global {
  interface Window {
    AppleID?: {
      auth: AppleAuth;
    };
  }
}

export type WebAppleSignInResult = {
  identityToken: string;
  nonce: string;
  email?: string;
  givenName?: string;
  familyName?: string;
};

let scriptLoadPromise: Promise<void> | null = null;
let preparedAuthConfigPromise: Promise<{
  rawNonce: string;
  config: AppleAuthConfig;
}> | null = null;

function createRandomString(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function sha256Hex(value: string): Promise<string> {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    throw new Error('Apple sign-in requires Web Crypto support.');
  }

  const bytes = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function getAppleWebClientId(): string {
  const configured = env.appleWebClientId;
  if (typeof configured === 'string' && configured.trim().length > 0) {
    return configured.trim();
  }

  return DEFAULT_APPLE_WEB_CLIENT_ID;
}

function getAppleWebRedirectUri(): string {
  const configured = env.appleWebRedirectUrl;
  if (typeof configured === 'string' && configured.trim().length > 0) {
    return configured.trim();
  }

  if (Capacitor.isNativePlatform()) {
    return DEFAULT_NATIVE_APPLE_WEB_REDIRECT_URL;
  }

  if (typeof window === 'undefined') {
    return DEFAULT_APPLE_WEB_REDIRECT_PATH;
  }

  return buildExternalWebUrl(DEFAULT_APPLE_WEB_REDIRECT_PATH);
}

async function loadAppleAuthScript(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Apple sign-in can only be used in the browser.');
  }

  if (window.AppleID?.auth) {
    return;
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(APPLE_AUTH_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Apple sign-in could not be loaded.')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.id = APPLE_AUTH_SCRIPT_ID;
    script.src = APPLE_AUTH_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Apple sign-in could not be loaded.'));
    document.head.appendChild(script);
  }).finally(() => {
    if (!window.AppleID?.auth) {
      scriptLoadPromise = null;
    }
  });

  await scriptLoadPromise;

  if (!window.AppleID?.auth) {
    scriptLoadPromise = null;
    throw new Error('Apple sign-in is unavailable in this browser.');
  }
}

async function createAppleAuthConfig(): Promise<{
  rawNonce: string;
  config: AppleAuthConfig;
}> {
  await loadAppleAuthScript();

  const rawNonce = createRandomString();
  const hashedNonce = await sha256Hex(rawNonce);
  const state = createRandomString();

  return {
    rawNonce,
    config: {
      clientId: getAppleWebClientId(),
      scope: 'name email',
      redirectURI: getAppleWebRedirectUri(),
      state,
      nonce: hashedNonce,
      usePopup: true,
    },
  };
}

export async function prepareWebAppleSignIn(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    return;
  }

  if (!preparedAuthConfigPromise) {
    preparedAuthConfigPromise = createAppleAuthConfig().catch((error) => {
      preparedAuthConfigPromise = null;
      throw error;
    });
  }

  const prepared = await preparedAuthConfigPromise;
  const auth = window.AppleID?.auth;
  if (!auth) {
    preparedAuthConfigPromise = null;
    throw new Error('Apple sign-in is unavailable in this browser.');
  }

  auth.init(prepared.config);
}

export async function signInWithWebApple(): Promise<WebAppleSignInResult> {
  const prepared = await (preparedAuthConfigPromise ?? createAppleAuthConfig());
  preparedAuthConfigPromise = null;

  const auth = window.AppleID?.auth;
  if (!auth) {
    throw new Error('Apple sign-in is unavailable in this browser.');
  }

  auth.init(prepared.config);

  const response = await auth.signIn();
  const identityToken = response.authorization?.id_token;

  void prepareWebAppleSignIn().catch(() => {
    // The next click can recreate the prepared config if background prewarm fails.
  });

  if (!identityToken) {
    throw new Error('Apple sign-in could not be completed.');
  }

  return {
    identityToken,
    nonce: prepared.rawNonce,
    email: response.user?.email,
    givenName: response.user?.name?.firstName,
    familyName: response.user?.name?.lastName,
  };
}
